/**
 * Who else is in this programme, and what they have open.
 *
 * One connection per open programme, negotiated through GraphQL so the
 * client-facing surface stays GraphQL even though the socket does not.
 *
 * @module platform/realtime-context
 */

import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

import { PlatformApiError } from "@/platform/api-error";
import type { ProgrammePlatformClient, RealtimeConnection } from "@/platform/client";
import type { NarrativeEvent } from "@/platform/narrative-doc";
import { backoffDelayMs } from "@/utils/backoff";

/** What one editor publishes about themselves. */
export interface EditorPresenceState {
  /** The client id the editor's connection and leases are taken under. */
  clientId: string;
  /** Display name, as the platform knows it. */
  name: string;
  /**
   * Who they are on the platform, for their profile photo.
   *
   * Sent to peers who already hold their name and share their tenant and
   * programme, so it reveals nothing the room does not already have.
   */
  userId?: string;
  /** The module they have open, if any. */
  moduleId?: string;
  /**
   * Whether they can actually change what they are looking at.
   *
   * Not the route: a lead sitting on an edit step is read-only while another
   * holds the structural lease, and claiming otherwise is worse than saying
   * nothing. This is the value that decides whether the pane is inert.
   */
  activity: "editing" | "viewing";
}

/** What the local editor supplies; the hook adds the client id. */
export type SelfPresence = Omit<EditorPresenceState, "clientId">;

/** A module whose stored copy moved on since this client last read it. */
export interface SavedElsewhere {
  moduleId: string;
  moduleContentHash: string;
}

/**
 * Presence has more states than two.
 *
 * An empty list reads as "nobody else is here", which is exactly the state in
 * which somebody confidently starts editing. The ignorant states are kept
 * apart because they call for different things: `unavailable` is a standing
 * fact about the deployment, `offline` is a channel we expect to have and are
 * retrying, and `failed` is one the retries gave up on, which a reload may
 * yet recover.
 */
export type PresenceStatus = "present" | "unavailable" | "offline" | "failed";

export interface RealtimeValue {
  status: PresenceStatus;
  /** Everybody else in this programme, excluding the local client. */
  peers: EditorPresenceState[];
  /** What this client publishes about itself; absent outside a programme. */
  self?: SelfPresence;
  /** Modules somebody else has saved since this client last read them. */
  savedElsewhere: readonly SavedElsewhere[];
  /** Drop a module from {@link savedElsewhere} once its copy has caught up. */
  acknowledgeSaved: (moduleId: string) => void;
  /** Relay one narrative document update to the other editors. */
  publishNarrative: (update: string) => void;
  /**
   * Listen for narrative traffic. Returns the unsubscribe.
   *
   * A subscription rather than a state array: the document applies each update
   * as it lands and Yjs already ignores one it has seen, so holding them would
   * be an append-only list of every peer keystroke for the life of the session,
   * re-walked in full on each new frame.
   */
  subscribeNarrative: (listener: (event: NarrativeEvent) => void) => () => void;
}

const RealtimeContext = React.createContext<RealtimeValue | null>(null);

const UNAVAILABLE: RealtimeValue = {
  status: "unavailable",
  peers: [],
  savedElsewhere: [],
  acknowledgeSaved: () => undefined,
  publishNarrative: () => undefined,
  subscribeNarrative: () => () => undefined,
};

/**
 * What travels on one programme's group.
 *
 * Presence goes client-to-group directly, with no server round trip; a save
 * and a departure come from the server, which is the only party that knows
 * either for certain. A `hello` asks the room to answer with its own state,
 * which is how a newcomer learns who was already here.
 */
type GroupMessage =
  | {
      kind: "presence";
      clientId: string;
      name: string;
      userId?: string;
      moduleId?: string;
      activity?: "editing" | "viewing";
      hello?: boolean;
    }
  | { kind: "leave"; clientId: string }
  | { kind: "saved"; moduleId: string; moduleContentHash: string }
  | { kind: "narrative"; update: string };

/** A non-empty string field of an untrusted object, or nothing. */
function stringField(record: Record<string, unknown>, key: string): string | undefined {
  const value = record[key];
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

/**
 * Read a group payload, or nothing when it is not one.
 *
 * Peers publish straight to the group, so these bytes are attacker-controlled
 * all the way down: a throw here would escape into an event listener nobody is
 * catching, and an unchecked name would reach React as a child.
 *
 * @param data - The `data` field of a service envelope.
 * @returns The message, or undefined when it is not a well-formed one.
 */
export function parseGroupMessage(data: unknown): GroupMessage | undefined {
  if (!data || typeof data !== "object") {
    return undefined;
  }
  const record = data as Record<string, unknown>;
  switch (record.kind) {
    case "presence": {
      const clientId = stringField(record, "clientId");
      const name = stringField(record, "name");
      return clientId && name
        ? {
            kind: "presence",
            clientId,
            name,
            userId: stringField(record, "userId"),
            moduleId: stringField(record, "moduleId"),
            // Only the two literals. Anything else a peer sends is dropped, so
            // it reads as viewing rather than as an unrecognised claim.
            activity: record.activity === "editing" ? "editing" : "viewing",
            hello: record.hello === true,
          }
        : undefined;
    }
    case "leave": {
      const clientId = stringField(record, "clientId");
      return clientId ? { kind: "leave", clientId } : undefined;
    }
    case "saved": {
      const moduleId = stringField(record, "moduleId");
      const moduleContentHash = stringField(record, "moduleContentHash");
      return moduleId && moduleContentHash
        ? { kind: "saved", moduleId, moduleContentHash }
        : undefined;
    }
    case "narrative": {
      const update = stringField(record, "update");
      return update ? { kind: "narrative", update } : undefined;
    }
    default:
      return undefined;
  }
}

/** The subprotocol that lets a client publish straight to its group. */
const SUBPROTOCOL = "json.webpubsub.azure.v1";

/** Reconnect schedule: from a second, doubling with jitter, to half a minute. */
const RETRY = { baseMs: 1_000, ceilingMs: 30_000 };

/**
 * Reconnects before giving up, a little over two minutes in all.
 *
 * A channel that has not come back by then is not a blip: the service is
 * full, the identity cannot mint, or the deployment is broken. Retrying
 * forever would hide every one of those behind "Reconnecting".
 */
const RETRY_LIMIT = 8;

/**
 * Drive one programme's presence channel.
 *
 * Presence is published on change (join, leave, switching module) and never
 * on a timer. Billing counts outbound traffic in 2KB increments and a broadcast
 * to N subscribers costs N, so a 15-second heartbeat across three editors would
 * spend the free tier's whole daily allowance on one programme. Liveness comes
 * from the server instead, which announces a departed client to its room.
 *
 * @param client - Platform client, for the negotiate mutation.
 * @param programmeId - Programme to join, or null to stay disconnected.
 * @param clientId - The same client id the edit lease is acquired under.
 * @param self - What to publish about this client.
 * @returns The presence value for {@link RealtimeBoundary}.
 */
export function useRealtimePresence(
  client: ProgrammePlatformClient,
  programmeId: string | null,
  clientId: string,
  self: SelfPresence,
): RealtimeValue {
  const [status, setStatus] = useState<PresenceStatus>("offline");
  const [peers, setPeers] = useState<EditorPresenceState[]>([]);
  const [savedElsewhere, setSavedElsewhere] = useState<SavedElsewhere[]>([]);
  const narrativeListeners = useRef(new Set<(event: NarrativeEvent) => void>());
  const socketRef = useRef<WebSocket | null>(null);
  const groupRef = useRef<string | null>(null);
  // Null for a caller holding only `programme:read`, whose token carries no
  // narrative group. Publishing to one it is not a member of is refused by the
  // service, so this is what keeps the frame off the wire at all.
  const narrativeGroupRef = useRef<string | null>(null);
  const peersRef = useRef(new Map<string, EditorPresenceState>());
  const selfRef = useRef<SelfPresence>(self);

  /**
   * Publish one payload to a group this connection may send to.
   *
   * The group is a parameter rather than the presence room because narrative
   * frames go to a group of their own, which a reader's token does not carry.
   *
   * @param group - The group to publish to, or null when there is none.
   * @param payload - The frame to send.
   */
  const sendToGroup = useCallback((group: string | null, payload: GroupMessage) => {
    const socket = socketRef.current;
    if (!socket || !group || socket.readyState !== WebSocket.OPEN) {
      return;
    }
    socket.send(
      JSON.stringify({
        type: "sendToGroup",
        group,
        // The sender is a member of its own group; without this it would hear
        // its own announcements back.
        noEcho: true,
        dataType: "json",
        data: payload,
      }),
    );
  }, []);

  const notifyNarrative = useCallback((event: NarrativeEvent) => {
    narrativeListeners.current.forEach((listener) => {
      listener(event);
    });
  }, []);

  const subscribeNarrative = useCallback((listener: (event: NarrativeEvent) => void) => {
    const listeners = narrativeListeners.current;
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, []);

  /** Tell the room what this client has open, asking it to answer if `hello`. */
  const announceSelf = useCallback(
    (hello: boolean) => {
      sendToGroup(groupRef.current, { kind: "presence", clientId, ...selfRef.current, hello });
    },
    [clientId, sendToGroup],
  );

  useEffect(() => {
    if (!programmeId) {
      return undefined;
    }
    const joining = programmeId;
    const room = peersRef.current;

    let disposed = false;
    let attempt = 0;
    let reconnected = false;
    let retry: ReturnType<typeof setTimeout> | undefined;

    const publishPeers = (): void => {
      setPeers(Array.from(room.values()));
    };

    // The token expires after an hour and a laptop lid closes long before
    // that, so a channel with no reconnect is a channel that ends the session
    // ignorant. Re-negotiating rather than reopening is deliberate: the URL
    // carries the expired token.
    const scheduleRetry = (reconnect: () => Promise<void>): void => {
      if (disposed) {
        return;
      }
      if (attempt >= RETRY_LIMIT) {
        setStatus("failed");
        return;
      }
      const delay = backoffDelayMs(attempt, RETRY);
      attempt += 1;
      retry = setTimeout(() => {
        reconnect().catch(() => undefined);
      }, delay);
    };

    const receive = (payload: GroupMessage, group: string | undefined): void => {
      if (payload.kind === "presence") {
        if (payload.clientId === clientId) {
          return;
        }
        room.set(payload.clientId, {
          clientId: payload.clientId,
          name: payload.name,
          userId: payload.userId,
          moduleId: payload.moduleId,
          // An older client says nothing, and viewing is the claim that cannot
          // mislead.
          activity: payload.activity ?? "viewing",
        });
        publishPeers();
        if (payload.hello) {
          announceSelf(false);
        }
      } else if (payload.kind === "leave") {
        if (room.delete(payload.clientId)) {
          publishPeers();
        }
      } else if (payload.kind === "narrative") {
        // The narrative group is gated on the way out by the token's roles. A
        // reader can still publish to presence, which everyone is in, so the
        // frame's own group is what decides whether it reaches a document.
        if (group !== narrativeGroupRef.current) {
          return;
        }
        notifyNarrative({ kind: "update", update: payload.update });
      } else {
        setSavedElsewhere((current) => [
          ...current.filter((entry) => entry.moduleId !== payload.moduleId),
          { moduleId: payload.moduleId, moduleContentHash: payload.moduleContentHash },
        ]);
      }
    };

    async function connect(): Promise<void> {
      // Presence is a nicety; it must never take the editor down with it. A
      // network failure and an adapter that does not implement it read the
      // same way: we do not know who else is here, so ask again later.
      let connection: RealtimeConnection | undefined;
      try {
        connection = await client.negotiateRealtimeConnection({ programmeId: joining, clientId });
      } catch (error) {
        if (!disposed && error instanceof PlatformApiError && error.status === 404) {
          // A programme hidden or gone answers the same on every retry. Only a
          // reload, which reads the route again, can change the answer, and
          // the room this client was in is no longer one it may know about.
          groupRef.current = null;
          narrativeGroupRef.current = null;
          room.clear();
          publishPeers();
          setSavedElsewhere([]);
          setStatus("failed");
          return;
        }
      }
      if (disposed) {
        return;
      }
      if (!connection) {
        setStatus("offline");
        scheduleRetry(connect);
        return;
      }
      if (!connection.available || !connection.url || !connection.group) {
        // The deployment carries no presence channel. Retrying would never
        // find one, so this is where the client stops asking.
        setStatus("unavailable");
        return;
      }
      groupRef.current = connection.group;
      narrativeGroupRef.current = connection.narrativeGroup ?? null;
      let socket: WebSocket;
      try {
        socket = new WebSocket(connection.url, SUBPROTOCOL);
      } catch {
        setStatus("offline");
        scheduleRetry(connect);
        return;
      }
      socketRef.current = socket;
      socket.addEventListener("open", () => {
        attempt = 0;
        // Whoever was here while this client was away may have left since.
        // The answers to this hello rebuild the room from who is really there.
        room.clear();
        publishPeers();
        setStatus("present");
        announceSelf(true);
        // The room's hello has no counterpart for the document, and a reopened
        // socket carries nothing that was published while it was down.
        if (reconnected) {
          notifyNarrative({ kind: "resync" });
        }
        reconnected = true;
      });
      socket.addEventListener("message", (event) => {
        let envelope: { type?: string; group?: string; data?: unknown };
        try {
          envelope = JSON.parse(String(event.data)) as {
            type?: string;
            group?: string;
            data?: unknown;
          };
        } catch {
          return;
        }
        if (envelope.type !== "message") {
          return;
        }
        const payload = parseGroupMessage(envelope.data);
        if (payload) {
          receive(payload, envelope.group);
        }
      });
      // A lost room, not an empty one. A failed channel that rendered as
      // "nobody else is here" is the state in which somebody starts editing.
      socket.addEventListener("close", () => {
        if (!disposed) {
          setStatus("offline");
          scheduleRetry(connect);
        }
      });
      socket.addEventListener("error", () => {
        if (!disposed) {
          setStatus("offline");
        }
      });
    }

    connect().catch(() => undefined);

    return () => {
      disposed = true;
      clearTimeout(retry);
      sendToGroup(groupRef.current, { kind: "leave", clientId });
      socketRef.current?.close();
      socketRef.current = null;
      groupRef.current = null;
      narrativeGroupRef.current = null;
      room.clear();
      setStatus("offline");
      setPeers([]);
      setSavedElsewhere([]);
    };
  }, [announceSelf, client, clientId, notifyNarrative, programmeId, sendToGroup]);

  const acknowledgeSaved = useCallback((moduleId: string) => {
    setSavedElsewhere((current) => current.filter((entry) => entry.moduleId !== moduleId));
  }, []);

  // What this client has open changes far more often than the connection does,
  // and the socket opens after a round trip the caller's first render does not
  // wait for. Publishing here rather than from the connect effect is what keeps
  // a name that resolves late from being broadcast as its own placeholder.
  const {
    name: selfName,
    userId: selfUserId,
    moduleId: selfModuleId,
    activity: selfActivity,
  } = self;
  useEffect(() => {
    selfRef.current = {
      name: selfName,
      userId: selfUserId,
      moduleId: selfModuleId,
      activity: selfActivity,
    };
    announceSelf(false);
  }, [announceSelf, selfActivity, selfName, selfModuleId, selfUserId]);

  const publishNarrative = useCallback(
    (update: string) => sendToGroup(narrativeGroupRef.current, { kind: "narrative", update }),
    [sendToGroup],
  );

  return useMemo(
    () =>
      programmeId
        ? {
            status,
            peers,
            self: {
              name: selfName,
              userId: selfUserId,
              moduleId: selfModuleId,
              activity: selfActivity,
            },
            savedElsewhere,
            acknowledgeSaved,
            publishNarrative,
            subscribeNarrative,
          }
        : UNAVAILABLE,
    [
      acknowledgeSaved,
      peers,
      programmeId,
      publishNarrative,
      savedElsewhere,
      selfActivity,
      selfModuleId,
      selfName,
      selfUserId,
      status,
      subscribeNarrative,
    ],
  );
}

export function RealtimeBoundary({
  value,
  children,
}: {
  value: RealtimeValue;
  children: React.ReactNode;
}) {
  return <RealtimeContext.Provider value={value}>{children}</RealtimeContext.Provider>;
}

/** Presence for the surrounding programme, or an honest "unavailable" outside one. */
export function useRealtime(): RealtimeValue {
  return useContext(RealtimeContext) ?? UNAVAILABLE;
}
